import { Component, OnInit, AfterViewInit, ChangeDetectorRef, ViewChildren, QueryList, Inject, Optional, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { OwlDateTimeInputDirective } from '@danielmoncada/angular-datetime-picker';
import { PromotionCodeDataService } from '../../../promotion-codes/services/promotion-code-data.service';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { forkJoin, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import * as moment from 'moment-timezone';
import { Game } from '@core/models/game.model';
import { GameProviderHttpService } from '@core/services/game-provider-http.service';

@Component({
  selector: 'kt-promotion',
  templateUrl: './basic-setting.component.html',
  styleUrls: ['./basic-setting.component.scss']
})
export class BasicSettingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(OwlDateTimeInputDirective) datePicker: QueryList<OwlDateTimeInputDirective<any>>;
  promotionCodeDropdownList = [];
  promotionCodeDropdownSettings = {};
  promotionCodeSelectedItems = [];
  form: FormGroup;
  
  isPromotionLoading = false;
  dateTimeStack = {
    valid_from_date: null,
    valid_to_date: null
  };

  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  clientOffset = moment().utcOffset() * 60 * 1000;
  offset = moment.tz(this.timezone).utcOffset() * 60 * 1000;
  dateTimeFormat = 'yyyy-MM-dd HH:mm:';
  namePreview: string = '';

  freeSpinGameDropdownList : Array<Game>;
  freeSpinGameSelectedList : Array<Game>;
  freeSpingameProvidersDropdownSettings = {};
  freeSpinGameDropdownSettings = {};
  freeSpinGameDisabled: boolean = true;

  // multi-provider free spin (AIO-71840)
  freeSpinGameProvidersDropdownList : Array<any> = [];
  allFreeSpinGameProvidersDropdownList : Array<any> = []; // unfiltered copy for re-filtering by sub type
  freeSpinGameProvidersSelectedList : Array<any> = [];
  freeSpinGameProviderDisabled: boolean = true;
  // Spinners for the provider / game lists. The *Disabled flags above already block interaction
  // while they load (both start true), so these only drive the loading indicator.
  isFreeSpinProviderLoading = false;
  isFreeSpinGameLoading = false;
  hideRecurring = false; // Welcome (promo_sub_type 1) hides Recurring — mirrors v1

  // Reset Day options (AIO-71840). Weekly picks a weekday (1-7), Monthly a day-of-month (1-31).
  weekdayOptions = Object.keys(moment.weekdays()).map(el => {
    const id = (+el + 1);
    const name = String(moment.weekdays(id)).substring(0, 3);
    return { id, name };
  });
  monthDayOptions = this.buildMonthDayOptions();
  
  private datePickerSubscription = new Subscription();
  private formSubscription: Subscription;
  private saveSubscription: Subscription;
  private promoSubscription = new Subscription();

  constructor(
    private promotionCodeDataService: PromotionCodeDataService,
    private eventEmitterService: EventEmitterService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private gameProviderHttpService: GameProviderHttpService,
    @Optional() @Inject('initialData') public initialData: any,
    @Optional() @Inject('isEditMode') public isEditMode: boolean
  ) { 
    this.initialData = initialData;
  }

  async ngOnInit() {
    this.formInit();
    this.eventEmitterService.onBasicSettingData(this.form.value);
    this.formSubscription = this.form.valueChanges.subscribe(values => {
      this.eventEmitterService.onBasicSettingData(values);
    });
    this.setupDropdownSettings();
    this.loadPromotionCodes();
    try {
      await this.loadFreeSpinGameList();
    } catch (err) {
      console.error('Failed to load free spin game/provider list', err);
    }

    setTimeout(() => {
      this.freeSpinGameSelectedList = [...this.freeSpinGameSelectedList];
      this.cdr.detectChanges(); 
    });

    if (this.initialData?.name) {
      this.namePreview = this.initialData.name
    } else {
      this.namePreview = '*Name';
    }
  }
  
  ngAfterViewInit() {
    this.setupDatePickers();
  }
  
  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
    if (this.datePickerSubscription) {
      this.datePickerSubscription.unsubscribe();
    }

    if (this.promoSubscription) {
      this.promoSubscription.unsubscribe();
    }
  }

  // Free Spin: a new promotion always starts Active, so Status is locked on create (mirrors the
  // 3.2.1 dialog) and only becomes editable on edit. Provider Promotion (BTG/PP) is unaffected.
  get statusLocked(): boolean {
    return !this.isEditMode && this.initialData?.promotion_type == 'Free Spin';
  }

  onChangeStatus($event, type) {
    // The template blocks the input, but keep the handler honest: a locked Status must stay Active.
    if (type === 'status' && this.statusLocked) {
      $event.target.checked = true;
      return;
    }

    const  isChecked = $event.target.checked ? 1 : 0;
    this.form.patchValue({
      [type]: isChecked
    });
  }

  private formInit() {
    let code = this.initialData?.code || null;
    let name = this.initialData?.name || null;
    let promotion_ids = this.initialData?.promotion_ids || [];
    let valid_from = this.isValidDate(this.initialData?.valid_from) ? moment(this.initialData?.valid_from).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss') : null;
    let valid_to = this.isValidDate(this.initialData?.valid_to) ? moment(this.initialData?.valid_to).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss') : null;
    // Without this local, `status` resolved to the global window.status ('') — the control was built
    // with an empty value and only recovered because the wizard patches initialData over it. (AIO-81070)
    let status = this.initialData?.status !== undefined ? this.initialData.status : 1;
    let restrict_claim_round_active = this.initialData?.restrict_claim_round_active !== undefined ? this.initialData.restrict_claim_round_active : 0;
    let restrict_same_provider_launch = this.initialData?.restrict_same_provider_launch !== undefined ? this.initialData.restrict_same_provider_launch : 0;
    let free_spin_game_code = this.initialData?.free_spin_game_code ? this.initialData.free_spin_game_code : null;
    let free_spin_game_provider_id = this.initialData?.free_spin_game_provider_id ?? null;
    // Number(): the Type <select> binds with [ngValue], which matches strictly — a string '2'
    // from the API would leave the dropdown blank.
    let promo_sub_type = Number(this.initialData?.promo_sub_type ?? 1); // 1: Welcome, 2: Reload
    let recurring = this.initialData?.recurring ?? 0; // 0: One Time, 1: Recurring
    let reset_frequency = this.initialData?.reset_frequency ?? 0; // 1: Daily, 2: Weekly, 3: Monthly
    // frequency_type / frequency have no UI: nothing in the API reads them after creation (they are
    // written on store/update and only ever read back for display), so the picker was removed.
    // Create falls through to 1 = Daily, where the API makes `frequency` optional
    // ('frequency' => 'required_unless:frequency_type,1'). Edit keeps whatever is already stored,
    // so an existing 2 (Weekly) / 3 (Monthly) promotion is not silently rewritten.
    let frequency_type = this.initialData?.frequency_type ?? 1; // 1: Daily, 2: Weekly, 3: Monthly
    let frequency = this.initialData?.frequency ?? []; // selected weekdays / month days

    // this.dateTimeStack.valid_from = valid_from;
    // this.dateTimeStack.valid_to = valid_to;

    this.form = new FormGroup({
      code: new FormControl(code, [Validators.required, Validators.pattern('[-_a-zA-Z0-9]*')]),
      name: new FormControl(name, [Validators.required]),
      promotion_ids: new FormControl(promotion_ids),
      valid_from: new FormControl(valid_from, [Validators.required]),
      valid_to: new FormControl(valid_to, [Validators.required]),
      status: new FormControl(status, [Validators.required]),
      restrict_claim_round_active: new FormControl(restrict_claim_round_active),
      restrict_same_provider_launch: new FormControl(restrict_same_provider_launch),
      free_spin_game_code: new FormControl(free_spin_game_code),
    });

    // Free Spin only controls — added conditionally so they never leak into PP / BTG payloads
    if (this.initialData?.promotion_type == 'Free Spin') {
      this.form.addControl('promo_sub_type', new FormControl(promo_sub_type));
      this.form.addControl('free_spin_game_provider_id', new FormControl(free_spin_game_provider_id, Validators.required));
      this.form.addControl('recurring', new FormControl(recurring));
      this.form.addControl('reset_frequency', new FormControl(reset_frequency));
      this.form.addControl('frequency_type', new FormControl(frequency_type, Validators.required));
      this.form.addControl('frequency', new FormControl(frequency));
      this.form.addControl('reward_validity', new FormControl(this.initialData?.reward_validity ?? 0)); // 0 = unlimited
      // Validity = claim window in days (v1 range 1–30). Number(): the API can return it as a string.
      this.form.addControl('validity', new FormControl(Number(this.initialData?.validity ?? 30)));
      // Number(): the reset-day chips compare with === against the numeric option ids, and the API
      // can return this as a string.
      this.form.addControl('reset_day', new FormControl(Number(this.initialData?.reset_day ?? 0))); // weekday / day-of-month for recurring reset
      this.hideRecurring = promo_sub_type == 1; // Welcome hides Recurring
    }

    if (this.initialData) {
      if (this.initialData.promotion_type == 'PP Promotion' || this.initialData.promotion_type == 'Free Spin') {
        this.form.get('free_spin_game_code').setValidators(Validators.required);
        this.form.get('free_spin_game_code').updateValueAndValidity();
      }
      else {
        this.freeSpinGameSelectedList = [];
        this.form.patchValue({
          free_spin_game_code: null,
        });
      }

      this.form.patchValue({
        ...this.initialData,
        valid_from_date: this.isValidDate(this.initialData?.valid_from) ? moment(new Date(new Date(this.initialData?.valid_from).getTime() + this.offset - this.clientOffset)).format('YYYY-MM-DD HH:mm:ss') : null,
        valid_to_date: this.isValidDate(this.initialData?.valid_to) ? moment(new Date(new Date(this.initialData?.valid_to).getTime() + this.offset - this.clientOffset)).format('YYYY-MM-DD HH:mm:ss') : null,
      });
      this.dateTimeStack = {
        valid_from_date: this.initialData?.valid_from !== null ? new Date(new Date(this.initialData?.valid_from).getTime() + this.offset - this.clientOffset) : null,
        valid_to_date: this.initialData?.valid_to !== null ? new Date(new Date(this.initialData?.valid_to).getTime() + this.offset - this.clientOffset) : null,
      };
      this.cdr.detectChanges()
    }

    // Free Spin: "Claim If Bonus Round Active" is always ticked (and disabled in the UI)
    if (this.initialData?.promotion_type == 'Free Spin') {
      this.form.patchValue({ restrict_claim_round_active: 1 });
    }

    // Runs after the initialData patch above: Status is locked on create, so force Active. Without
    // this, a 0 carried over from the Provider Promotion tab would leave the locked toggle on OFF.
    if (this.statusLocked) {
      this.form.patchValue({ status: 1 });
    }

    setTimeout(() => {
      this.eventEmitterService.onBasicSettingData(this.form.value);
    });
  }

  private setupDropdownSettings() {
    this.promotionCodeDropdownSettings = {
      singleSelection: false,
      text: 'Please Select',
      enableFilterSelectAll: false,
      enableSearchFilter: true,
      classes: 'dropdown',
      maxHeight: 200,
      primaryKey: 'id',
      labelKey: 'labelKey',
      noDataLabel: '',
      showCheckbox: false
    };

    if (this.initialData?.promotion_type == 'PP Promotion' || this.initialData?.promotion_type == 'Free Spin') {
      this.freeSpingameProvidersDropdownSettings = {
        text: 'Please Select',
        enableFilterSelectAll: false,
        enableSearchFilter: true,
        classes: 'dropdown',
        primaryKey: 'id',
        labelKey: 'labelKey',
        noDataLabel: '',
        maxHeight: 150,
        showCheckbox: false,
        singleSelection: true,
      };
      this.freeSpinGameDropdownSettings = {
        text: 'Please Select',
        enableFilterSelectAll: false,
        enableSearchFilter: true,
        classes: 'dropdown',
        primaryKey: 'code',
        labelKey: 'labelKey',
        // lazyLoading: true,
        noDataLabel: '',
        maxHeight: 150,
        showCheckbox: false,
        singleSelection: true,
      }
    }
  }

  private loadPromotionCodes() {
      this.isPromotionLoading = true;

      const handleDropdown = (list: any[]) => {
          list.map(elm => {
              elm['labelKey'] = elm.code + ' - ' + elm.name;
          });
          if (this.isEditMode && this.initialData?.id) {
              const currentItem = list.find(v => v.id === this.initialData.id);
              if (currentItem) {
                  const codePrefix = currentItem.code.replace(/_PHASE_\d+$/, '_PHASE_');
                  list = list.filter(v => !v.code.startsWith(codePrefix));
              }
          }

          this.promotionCodeDropdownList = list;
          this.promotionCodeSelectedItems = [];
          const selectedIds = this.form.get('promotion_ids')?.value || this.initialData?.promotion_ids || [];
          if (selectedIds.length > 0) {
              selectedIds.forEach((id: any) => {
                  const item = this.promotionCodeDropdownList.find(v => v.id === id);
                  if (item) this.promotionCodeSelectedItems.push(item);
              });
              this.form.patchValue({ promotion_ids: selectedIds });
          }

          this.isPromotionLoading = false;
          this.cdr.detectChanges();
      };

      const storedList = localStorage.getItem('promotionCodeDropdownList');
      if (!storedList) {
          this.promoSubscription = this.promotionCodeDataService.getWithQuery(`?status=1&paginate=false&dropdown=true`).subscribe({
              next: (res) => {
                  handleDropdown(res);
                  localStorage.setItem('promotionCodeDropdownList', JSON.stringify(this.promotionCodeDropdownList));
              },
              error: () => {
                  this.isPromotionLoading = false;
                  this.cdr.detectChanges();
              }
          });
      } else {
          handleDropdown(JSON.parse(storedList));
      }
  }

  // Read-only labels for the locked Free Spin Game Provider / Games (shown like Code on edit).
  get freeSpinProviderLabel(): string {
    return (this.freeSpinGameProvidersSelectedList?.[0] as any)?.labelKey ?? '';
  }

  get freeSpinGameLabel(): string {
    return (this.freeSpinGameSelectedList?.[0] as any)?.labelKey ?? '';
  }

  // Plain async method — a failed await now rejects the returned promise (was wrapped in
  // `new Promise(async …)`, whose implicit catch swallowed rejections so the caller hung forever
  // with empty dropdowns). The caller (ngOnInit) try/catches to surface the failure.
  async loadFreeSpinGameList() {
    if (this.initialData?.promotion_type == 'PP Promotion') {
      // PP Promotion (unchanged) — single provider, hardcoded PP game list
      this.isFreeSpinGameLoading = true;
      try {
        this.freeSpinGameDropdownList = await this.gameProviderHttpService.getFreeSpinGameList('PP').toPromise();
      } finally {
        this.isFreeSpinGameLoading = false;
      }
      this.freeSpinGameDropdownList.map(function (elm: any) {
        elm['labelKey'] = elm.code + ' - ' + elm.name;
      });
      this.isEditMode ? this.freeSpinGameDisabled = true : this.freeSpinGameDisabled = false;
      this.freeSpinGameSelectedList = this.freeSpinGameDropdownList.find(x => x.code == this.initialData?.free_spin_game_code)
        ? [this.freeSpinGameDropdownList.find(x => x.code == this.initialData?.free_spin_game_code)]
        : [];

      this.cdr.detectChanges();
    } else if (this.initialData?.promotion_type == 'Free Spin') {
      // Free Spin (AIO-71840) — multi-provider: load the provider list first
      this.isFreeSpinProviderLoading = true;
      // A saved promotion's game list is fetched right after the providers, so mark Games as loading
      // from the start — otherwise it looks "ready but empty" for the whole provider request.
      this.isFreeSpinGameLoading = !!this.initialData?.free_spin_game_provider_id;
      try {
        this.freeSpinGameProvidersDropdownList = await this.gameProviderHttpService.getFreeSpinGameProvider().toPromise();
      } finally {
        this.isFreeSpinProviderLoading = false;
      }
      this.freeSpinGameProvidersDropdownList.map(function (elm) {
        elm['labelKey'] = elm.code + ' - ' + elm.name;
      });
      this.allFreeSpinGameProvidersDropdownList = [...this.freeSpinGameProvidersDropdownList];
      this.filterFreeSpinProvidersBySubType(); // Reload = PP-family only (mirrors v1)

      this.isEditMode ? this.freeSpinGameProviderDisabled = true : this.freeSpinGameProviderDisabled = false;
      this.freeSpinGameProvidersSelectedList = this.freeSpinGameProvidersDropdownList.find(x => x.id == this.initialData?.free_spin_game_provider_id)
        ? [this.freeSpinGameProvidersDropdownList.find(x => x.id == this.initialData?.free_spin_game_provider_id)]
        : [];

      // if a provider is preselected (edit mode), load that provider's game list
      if (this.freeSpinGameProvidersSelectedList.length > 0) {
        this.isFreeSpinGameLoading = true;
        this.cdr.detectChanges();
        try {
          this.freeSpinGameDropdownList = await this.gameProviderHttpService.getFreeSpinGameList(this.freeSpinGameProvidersSelectedList[0].code).toPromise();
        } finally {
          this.isFreeSpinGameLoading = false;
        }
        this.freeSpinGameDropdownList.map(function (elm: any) {
          elm['labelKey'] = elm.code + ' - ' + elm.name;
        });
        this.isEditMode ? this.freeSpinGameDisabled = true : this.freeSpinGameDisabled = false;
        this.freeSpinGameSelectedList = this.freeSpinGameDropdownList.find(x => x.code == this.initialData?.free_spin_game_code)
          ? [this.freeSpinGameDropdownList.find(x => x.code == this.initialData?.free_spin_game_code)]
          : [];
      } else {
        this.freeSpinGameSelectedList = [];
        this.isFreeSpinGameLoading = false; // no provider preselected — nothing to fetch
      }

      this.cdr.detectChanges();
    }
  }

  // provider changed → reset the game selection and load the new provider's game list
  onFreeSpinGameProviderChange(event: Array<any>) {
    this.freeSpinGameDropdownList = [];
    this.freeSpinGameSelectedList = [];
    this.form.patchValue({ free_spin_game_code: null });

    if (event.length > 0) {
      // Keep the Games control locked with a spinner until its new list arrives.
      this.freeSpinGameDisabled = true;
      this.isFreeSpinGameLoading = true;
      this.gameProviderHttpService.getFreeSpinGameList(event[0].code).subscribe({
        next: res => {
          this.freeSpinGameDropdownList = res;
          this.freeSpinGameDropdownList.map(function (elm: any) {
            elm['labelKey'] = elm.code + ' - ' + elm.name;
          });
          this.isFreeSpinGameLoading = false;
          this.freeSpinGameDisabled = false;
          this.cdr.detectChanges();
        },
        // On failure the global ApiErrorInterceptor shows the error dialog; clear stale options.
        error: () => {
          this.freeSpinGameDropdownList = [];
          this.isFreeSpinGameLoading = false;
          this.freeSpinGameDisabled = false;
          this.cdr.detectChanges();
        },
      });
    } else {
      this.freeSpinGameDisabled = true;
    }
  }

  // Type (Welcome/Reload) change — mirrors v1 free-spin behavior (AIO-71840)
  onPromoSubTypeChange() {
    const subType = +this.form.value.promo_sub_type;
    // Welcome (1): One Time only → hide Recurring; Reload (2): show Recurring
    this.hideRecurring = subType === 1;
    if (this.hideRecurring) {
      this.form.patchValue({ recurring: 0, reset_frequency: 0 });
    }
    // Reload allows PP-family providers only; reset the provider / game selection on change
    this.filterFreeSpinProvidersBySubType();
    this.freeSpinGameProvidersSelectedList = [];
    this.freeSpinGameSelectedList = [];
    this.freeSpinGameDropdownList = [];
    this.freeSpinGameDisabled = true;
    this.form.patchValue({ free_spin_game_provider_id: null, free_spin_game_code: null });
  }

  private filterFreeSpinProvidersBySubType() {
    const subType = +this.form.value.promo_sub_type;
    if (subType === 2) {
      // Reload: PP-family only (matches v1)
      this.freeSpinGameProvidersDropdownList = this.allFreeSpinGameProvidersDropdownList.filter(x => ['PP', 'PP2', 'PTI'].includes(x?.code));
    } else {
      this.freeSpinGameProvidersDropdownList = [...this.allFreeSpinGameProvidersDropdownList];
    }
  }

  // recurring / reset-day handlers (AIO-71840)
  onSetRecurring() {
    // The Recurring dropdown is the reset cycle (0=One Time, 1=Daily, 2=Weekly, 3=Monthly) and
    // binds to reset_frequency; recurring is derived — "One Time" means not recurring.
    // reset_day is only meaningful for Weekly/Monthly, so clear it when the cycle changes.
    //
    // Read the cycle from the form control, NOT from event.target.value: the options bind with
    // [ngValue], so Angular writes their DOM value as "<id>: <value>" (e.g. "3: 3"). Coercing that
    // with + yielded NaN, NaN > 0 was false, and recurring was pinned to 0 on every change —
    // which made storeV4/updateV4 reset reset_frequency and reset_day back to 0 server-side.
    const cycle = +this.form.value.reset_frequency;
    this.form.patchValue({ recurring: cycle > 0 ? 1 : 0, reset_day: 0 });
  }

  // Reset Day is a SINGLE value (API: 'reset_day' => 'nullable|integer'), so the chips behave like
  // radios — picking one replaces the previous. Clicking the selected chip again clears it.
  selectResetDay(id: number) {
    const current = +this.form.value.reset_day;
    this.form.patchValue({ reset_day: current === id ? 0 : id });
  }

  isResetDaySelected(id: number): boolean {
    return +this.form.value.reset_day === id;
  }

  // Weekdays for a weekly reset, days 1–31 for a monthly one.
  get resetDayOptions(): { id: number, name: any }[] {
    return +this.form.value.reset_frequency === 2 ? this.weekdayOptions : this.monthDayOptions;
  }

  private buildMonthDayOptions() {
    const result = [];
    // Day-of-month options for a monthly reset must always be 1–31, independent of the current month
    // (was moment().endOf('month') → Feb capped the list at 28/29).
    for (let i = 1; i <= 31; i++) {
      result.push({ id: i, name: i });
    }
    return result;
  }

  private setupDatePickers() {
    if (this.datePicker && this.datePicker.length >= 2) {
      this.datePickerSubscription = forkJoin([
        this.buildDatePicker(0, 'valid_from'),
        this.buildDatePicker(1, 'valid_to')
      ]).subscribe();
    }
  }
  
  private buildDatePicker(index: number, formKey: string) {
    return this.datePicker.toArray()[index].valueChange.pipe(
      map(res => this.datePipe.transform(res, index === 0 ? this.dateTimeFormat + '00' : this.dateTimeFormat + '59')),
      tap(date => {
        this.form.patchValue({ [formKey]: date });
      })
    );
  }

  isValidDate(date: Date | string) {
    if (moment(date).isValid()) {
      return true;
    }
    return false;
  }
  
  onCodeInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const newValue = input.value.replace(/[^a-zA-Z0-9-_]/g, '');

    if (input.value !== newValue) {
      input.value = newValue;
      input.dispatchEvent(new Event('input'));
    }
  }

  onPreviewInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.initialData?.name) {
      this.namePreview = this.initialData.name
    } else if (input.value) {
      this.namePreview = input.value;
    } else {
      this.namePreview = '*Name';
    }
  }

  // block minus & scientific notation on number inputs
  onNumberKeydown(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'e') {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData?.getData('text');

    if (pastedText && (pastedText.includes('-') || pastedText.toLowerCase().includes('e'))) {
      event.preventDefault();
    }
  }
}